import { useContext, useMemo } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import ReactTooltip from "components/ReactTooltip";
import { DataStoreContext } from "../DataStore/DataStoreContext";
import {
  getBackendChannelInfo,
  getBackendChannelTitle,
  getDeploymentEnvironmentInfo,
  getFrontendTargetInfo,
  resolveCurrentBackendChannel,
  resolveCurrentBackendSettings,
} from "./channelInfo";
import { ChannelBadgeSubLabel, ChannelLabel } from "./style";

const ChannelBadgeTooltip = styled(ReactTooltip)`
  &.fr-tooltip {
    font-size: 12px;
    line-height: 1.3;
    text-align: left;
  }
`;

const CurrentChannelBadge = ({ mobile = false }) => {
  const { state } = useContext(DataStoreContext);
  const location = useLocation();
  const deploymentEnvironmentInfo = useMemo(() => getDeploymentEnvironmentInfo(), []);
  const frontendTargetInfo = useMemo(() => getFrontendTargetInfo(), []);
  const { backendChannelTitle, backendChannelInfo } = useMemo(() => {
    const backendSettings = resolveCurrentBackendSettings({
      pathname: location.pathname,
      state,
    });
    const backendChannel = resolveCurrentBackendChannel({
      pathname: location.pathname,
      state,
    });

    return {
      backendChannelTitle: getBackendChannelTitle(backendChannel),
      backendChannelInfo: getBackendChannelInfo(backendSettings),
    };
  }, [location.pathname, state]);

  const tooltipId = !mobile ? "channelLabel" : null;
  const tooltipContent = useMemo(() => {
    const sections = [];

    if (deploymentEnvironmentInfo.title) {
      const environmentDescription = deploymentEnvironmentInfo.tooltip ? ` ${deploymentEnvironmentInfo.tooltip}` : "";
      sections.push(
        `<div><strong>${deploymentEnvironmentInfo.title}</strong> environment: ${environmentDescription}</div>`
      );
    }

    if (frontendTargetInfo.label) {
      sections.push(`<div><strong>${frontendTargetInfo.title}</strong> frontend: ${frontendTargetInfo.tooltip}</div>`);
    }

    if (backendChannelInfo?.title) {
      const backendUrl = backendChannelInfo.url ? ` ${backendChannelInfo.url}` : "";
      sections.push(`<div><strong>${backendChannelInfo.title}</strong> backend: ${backendUrl}</div>`);
    }

    if (backendChannelInfo?.title) {
      sections.push(`<div>Change the backend in User, Organization or Scenario Settings</div>`);
    }

    return sections.join("<br/>") || null;
  }, [deploymentEnvironmentInfo, frontendTargetInfo, backendChannelInfo]);

  const frontendTargetMainLabel =
    !deploymentEnvironmentInfo.label && frontendTargetInfo.labelPlacement === "main" ? frontendTargetInfo.label : null;
  const frontendTargetSubLabel =
    frontendTargetInfo.labelPlacement === "sub" ||
    (deploymentEnvironmentInfo.label && frontendTargetInfo.labelPlacement === "main")
      ? frontendTargetInfo.label
      : null;
  const mainLabel = deploymentEnvironmentInfo.label || frontendTargetMainLabel;

  if (!mainLabel && !frontendTargetSubLabel && !backendChannelTitle) return null;

  return (
    <ChannelLabel
      data-for={tooltipId || undefined}
      data-tip={tooltipId ? tooltipContent : undefined}
      $mobile={mobile}
      $withSubLabel={!!frontendTargetSubLabel || !!backendChannelTitle}
    >
      {mainLabel && <span>{mainLabel}</span>}
      {frontendTargetSubLabel && <ChannelBadgeSubLabel>{frontendTargetSubLabel}</ChannelBadgeSubLabel>}
      {backendChannelTitle && <ChannelBadgeSubLabel>{backendChannelTitle}</ChannelBadgeSubLabel>}
      {tooltipId && tooltipContent && (
        <ChannelBadgeTooltip place="right" positionStrategy="fixed" html id={tooltipId} />
      )}
    </ChannelLabel>
  );
};

export default CurrentChannelBadge;
